// src/pages/Blog.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiClock, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CTA from '../components/CTA';
import VideoTestimonials from '../components/VideoTestimonials';
import WhatsAppButton from '../components/WhatsAppButton';
import HomeBrandBanner from '../components/HomeBrandBanner';
import { mockBlogs } from '../data/blogsData';
import { useSiteContent } from '../context/SiteContentContext';

const Blog = () => {
  const { blogs } = useSiteContent();
  const activeBlogList = blogs && blogs.length > 0 ? blogs : mockBlogs;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Pagination calculation
  const totalPages = Math.ceil(activeBlogList.length / itemsPerPage);
  const indexOfLastBlog = currentPage * itemsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - itemsPerPage;
  const currentBlogs = activeBlogList.slice(indexOfFirstBlog, indexOfLastBlog);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="min-h-screen bg-transparent relative overflow-hidden select-none">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-32 pb-16 overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
          <video autoPlay loop muted playsInline className="object-cover w-full h-full opacity-100">
            <source src="/herovideo.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 w-full text-center mt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 bg-[#F2B135]/10 border border-[#F2B135]/20 px-4 py-1.5 rounded-full mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
            <span className="text-xs font-black text-[#F2B135] uppercase tracking-[0.2em] font-display">
              Latest Blogs
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-4xl sm:text-6xl font-black text-white leading-tight mb-6 font-display"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Explore Our <br className="hidden md:inline" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F2B135] to-[#ffaa00]">
              Latest Blogs &amp; Tech Insights
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed font-medium"
          >
            Deep dives into programming, UI/UX designs, SaaS systems, eCommerce platforms, and marketing campaigns to keep your business ahead of the curve.
          </motion.p>
        </div>
      </section>

      {/* BLOG CARDS GRID */}
      <section className="relative z-20 py-16 bg-transparent px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          <div className="mb-10 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display flex items-center gap-3 justify-center sm:justify-start" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="w-2.5 h-7 rounded-full bg-[#F2B135] inline-block" />
              Latest Blogs
            </h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {currentBlogs.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group relative rounded-2xl bg-white/[0.02] border border-white/10 p-4 transition-all duration-500 hover:border-[#F2B135]/40 hover:bg-[#F2B135]/5 hover:-translate-y-2 flex flex-col justify-between overflow-hidden shadow-2xl hover:shadow-[0_20px_50px_rgba(242,177,53,0.08)] block cursor-pointer"
                >
                  {/* Subtle Inner Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F2B135]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  <div className="space-y-4">
                    {/* Blog Card Image */}
                    <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-white/5">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />
                    </div>

                    {/* Date & Read Time */}
                    <div className="flex items-center gap-4 text-white/50 text-[11px] font-semibold uppercase tracking-wider">
                      <div className="flex items-center gap-1.5">
                        <FiCalendar className="text-[#F2B135]" />
                        {post.date}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FiClock className="text-[#F2B135]" />
                        {post.readTime}
                      </div>
                    </div>

                    {/* Title */}
                    <h3
                      className="text-white text-base sm:text-lg font-bold group-hover:text-[#F2B135] transition-colors duration-300 leading-snug tracking-tight font-display"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-white/60 text-xs sm:text-sm leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                  </div>

                  {/* Author Block */}
                  <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img
                        src="/favicon.png"
                        alt="Rojrztech"
                        className="w-5 h-5 rounded-full object-contain"
                      />
                      <span className="text-white/80 text-xs font-bold font-sans">
                        Rojrztech
                      </span>
                    </div>

                    {/* Arrow */}
                    <div className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-white/40 group-hover:border-[#F2B135] group-hover:bg-[#F2B135] group-hover:text-black transition-all duration-300">
                      ›
                    </div>
                  </div>

                </Link>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-16">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  currentPage === 1
                    ? 'border-white/5 text-white/20 cursor-not-allowed'
                    : 'border-white/20 text-white hover:border-[#F2B135] hover:text-[#F2B135]'
                }`}
              >
                <FiChevronLeft size={16} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-10 h-10 rounded-full font-bold text-sm transition-all duration-300 border ${
                    currentPage === pageNum
                      ? 'bg-[#F2B135] text-black border-[#F2B135] shadow-[0_0_15px_rgba(242,177,53,0.3)]'
                      : 'bg-transparent text-white border-white/20 hover:border-[#F2B135] hover:text-[#F2B135]'
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  currentPage === totalPages
                    ? 'border-white/5 text-white/20 cursor-not-allowed'
                    : 'border-white/20 text-white hover:border-[#F2B135] hover:text-[#F2B135]'
                }`}
              >
                <FiChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Home Brand Link Banner */}
          <HomeBrandBanner className="mt-16" />

        </div>
      </section>

      <VideoTestimonials />
      <CTA />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Blog;
